import * as THREE from 'three';
import type { CarriedProp, CharacterStyle, FacialHair, Footwear, HairStyle, Headwear, Outfit } from './characterStyle';
import { Joint, JOINT_PARENTS, type Proportions } from './skeleton';

/**
 * Shared figure geometry for the character rig (player, remote players, the fence) and the instanced
 * crowd (pedestrians, police). Every piece is authored once here, in joint-local space, with a *tone*
 * per vertex instead of a colour:
 *
 *   tone 0 / 1 / 2   palette channels A / B / C of the mesh kind (e.g. torso: coat / shirt / tie,
 *                    head: skin / hair / lips), resolved per instance in the crowd shader or baked
 *                    into vertex colours by the rig;
 *   tone ≥ 3         fixed colours (shoe soles, eyes, brass, white, leather, fur, chrome);
 *   shade            a multiplier on top (pockets, seams, eye sockets, the shadow under the jaw …).
 *
 * Pieces that only some characters have (lapels or a tunic front, a moustache, a cap or a trilby,
 * shoes or boots) carry a *variant* id: the crowd merges all variants of a kind into one geometry and
 * its shader collapses the ones an instance doesn't use (one draw call per kind); the rig builds only
 * the variants its style needs. Hair is one cap with four blend shapes (volume, length, fringe/quiff,
 * recede) mixed per character, so ten period styles cost one mesh.
 */

// --- tones -----------------------------------------------------------------------------------------

export const Tone = { A: 0, B: 1, C: 2, SOLE: 3, EYE: 4, BRASS: 5, WHITE: 6, LEATHER: 7, FUR: 8, CHROME: 9 } as const;
/** Fixed colours for tones 3… (sRGB hex). */
export const FIXED_TONES: readonly number[] = [0x17130f, 0x140e0b, 0xd8ae52, 0xe6e2d8, 0x1c1713, 0x8c7c64, 0xb4b8bc];

export type Detail = 'high' | 'mid';

// --- geometry builder ------------------------------------------------------------------------------

/** Accumulates indexed triangles with tone / shade / variant (and optional hair blend deltas). */
export class GeoBuilder {
  readonly pos: number[] = [];
  readonly nor: number[] = [];
  readonly tone: number[] = [];
  readonly vari: number[] = [];
  readonly idx: number[] = [];
  /** Hair blend shapes: 4 × xyz per vertex (only when `hair` is set). */
  readonly deltas: number[][] | null;
  /**
   * Variant bitmask stamped on everything added (-1 = always drawn): a piece shared by several
   * variants (a male jaw under every kind of facial hair) is stored once with several bits set.
   */
  mask = -1;
  /** Tag the following pieces with one variant id (-1 = always drawn). */
  set variant(v: number) {
    this.mask = v < 0 ? -1 : 1 << v;
  }
  get variant(): number {
    return this.mask;
  }
  /** Translation added to every piece (re-expressing a child/parent joint in the anchor's space). */
  ox = 0;
  oy = 0;
  oz = 0;
  hasVariants = false;

  constructor(hair = false) {
    this.deltas = hair ? [[], [], [], []] : null;
  }

  get vertexCount(): number {
    return this.pos.length / 3;
  }

  /** Append `g` (disposed afterwards) with one tone and shade, or a per-vertex shade function. */
  add(g: THREE.BufferGeometry, tone: number, shade = 1, shadeAt?: (x: number, y: number, z: number) => number): this {
    const p = g.getAttribute('position') as THREE.BufferAttribute;
    let n = g.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!n) {
      g.computeVertexNormals();
      n = g.getAttribute('normal') as THREE.BufferAttribute;
    }
    const base = this.vertexCount;
    for (let i = 0; i < p.count; i++) {
      const x = p.getX(i) + this.ox;
      const y = p.getY(i) + this.oy;
      const z = p.getZ(i) + this.oz;
      this.pos.push(x, y, z);
      this.nor.push(n.getX(i), n.getY(i), n.getZ(i));
      this.tone.push(tone, shadeAt ? shadeAt(p.getX(i), p.getY(i), p.getZ(i)) * shade : shade);
      this.vari.push(this.mask);
      if (this.deltas) for (const d of this.deltas) d.push(0, 0, 0);
    }
    const index = g.getIndex();
    if (index) for (let i = 0; i < index.count; i++) this.idx.push(base + index.getX(i));
    else for (let i = 0; i < p.count; i++) this.idx.push(base + i);
    if (this.mask >= 0) this.hasVariants = true;
    g.dispose();
    return this;
  }

  /** Crowd geometry: position, normal, aTone (+ aVar, + aD0…aD3 for hair). */
  toGeometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('aTone', new THREE.Float32BufferAttribute(this.tone, 2));
    if (this.hasVariants) g.setAttribute('aVar', new THREE.Float32BufferAttribute(this.vari, 1));
    if (this.deltas) this.deltas.forEach((d, i) => g.setAttribute(`aD${i}`, new THREE.Float32BufferAttribute(d, 3)));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    return g;
  }

  /**
   * Rig geometry: vertex colours from `channels` (linear RGB of tones A/B/C) and the fixed tones,
   * hair blend shapes applied with `blend`, variant-tagged pieces other than `keep` dropped.
   */
  bake(channels: readonly THREE.Color[], blend?: readonly number[], keep = -1): THREE.BufferGeometry {
    const nv = this.vertexCount;
    const map = new Int32Array(nv).fill(-1);
    const pos: number[] = [];
    const nor: number[] = [];
    const col: number[] = [];
    let k = 0;
    for (let i = 0; i < nv; i++) {
      const v = this.vari[i]!;
      if (v >= 0 && keep >= 0 && ((v >> keep) & 1) === 0) continue;
      map[i] = k++;
      let x = this.pos[i * 3]!;
      let y = this.pos[i * 3 + 1]!;
      let z = this.pos[i * 3 + 2]!;
      if (this.deltas && blend) {
        for (let d = 0; d < 4; d++) {
          const w = blend[d] ?? 0;
          if (w === 0) continue;
          x += this.deltas[d]![i * 3]! * w;
          y += this.deltas[d]![i * 3 + 1]! * w;
          z += this.deltas[d]![i * 3 + 2]! * w;
        }
      }
      pos.push(x, y, z);
      nor.push(this.nor[i * 3]!, this.nor[i * 3 + 1]!, this.nor[i * 3 + 2]!);
      const t = this.tone[i * 2]!;
      const s = this.tone[i * 2 + 1]!;
      const c = t < 3 ? channels[t] ?? FIXED_LINEAR[0]! : FIXED_LINEAR[t - 3] ?? FIXED_LINEAR[0]!;
      col.push(c.r * s, c.g * s, c.b * s);
    }
    const idx: number[] = [];
    for (let i = 0; i < this.idx.length; i += 3) {
      const a = map[this.idx[i]!]!;
      const b = map[this.idx[i + 1]!]!;
      const c = map[this.idx[i + 2]!]!;
      if (a < 0 || b < 0 || c < 0) continue;
      idx.push(a, b, c);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    g.setIndex(idx);
    return g;
  }
}

const FIXED_LINEAR: THREE.Color[] = FIXED_TONES.map((h) => new THREE.Color(h));

/** Where pieces for a joint go: the rig keeps one builder per joint, the crowd one per mesh kind. */
export type Sink = (joint: number) => GeoBuilder;

/**
 * Offset of `joint`'s origin in `anchor`'s space at rest (no rotations), so a crowd mesh anchored on
 * one joint can carry pieces authored on its parent or child (the spine on the chest, the neck on the
 * head, shoulder caps on the chest).
 */
export function restOffset(rest: Float32Array, joint: number, anchor: number): [number, number, number] {
  const chain = (j: number): number[] => {
    const c: number[] = [];
    for (let k = j; k >= 0; k = JOINT_PARENTS[k]!) c.push(k);
    return c;
  };
  const cj = chain(joint);
  const ca = chain(anchor);
  const sum = (list: number[], stop: number): [number, number, number] => {
    const s: [number, number, number] = [0, 0, 0];
    for (const k of list) {
      if (k === stop) break;
      s[0] += rest[k * 3]!;
      s[1] += rest[k * 3 + 1]!;
      s[2] += rest[k * 3 + 2]!;
    }
    return s;
  };
  const common = cj.find((k) => ca.includes(k)) ?? 0;
  const a = sum(cj, common);
  const b = sum(ca, common);
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

// --- primitives --------------------------------------------------------------------------------------

const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d);
const cyl = (rt: number, rb: number, h: number, seg = 8, open = false): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg, 1, open);
const ball = (r: number, w = 8, h = 6): THREE.BufferGeometry => new THREE.SphereGeometry(r, w, h);

/** One station of a lathe: height, radius (X) and an optional depth radius (Z, default = r). */
export interface Ring {
  y: number;
  r: number;
  rz?: number;
  /** Shade at this station (interpolated between stations). */
  s?: number;
  /** Offset of the ring centre (a belly forward, a calf back). */
  dz?: number;
}

/**
 * A smooth lofted surface of revolution through `rings` (third character pass): limbs, torsos and
 * coat skirts are shaped sections, not cylinders, so they shade with a highlight and a dark turn
 * instead of a flat band per facet. Shared vertices round the rings give smooth normals; `wave`
 * perturbs a ring's radius per angle (hem folds). Caps are fans on the end rings.
 */
export function lathe(rings: readonly Ring[], seg: number, opts: { top?: boolean; bottom?: boolean; wave?: (angle: number, i: number) => number; shade?: number[] } = {}): THREE.BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  const shade = opts.shade;
  for (let i = 0; i < rings.length; i++) {
    const ring = rings[i]!;
    const rz = ring.rz ?? ring.r;
    for (let k = 0; k < seg; k++) {
      const a = (k / seg) * Math.PI * 2;
      const w = opts.wave ? opts.wave(a, i) : 0;
      pos.push(Math.sin(a) * (ring.r + w), ring.y, Math.cos(a) * (rz + w) + (ring.dz ?? 0));
      if (shade) shade.push(ring.s ?? 1);
    }
  }
  // Sides: wound so the faces look outward whichever way the stations run (up or down).
  const down = (rings[rings.length - 1]?.y ?? 0) < (rings[0]?.y ?? 0);
  for (let i = 0; i < rings.length - 1; i++) {
    for (let k = 0; k < seg; k++) {
      const a = i * seg + k;
      const b = i * seg + ((k + 1) % seg);
      const c = a + seg;
      const d = b + seg;
      if (down) idx.push(a, c, b, b, c, d);
      else idx.push(a, b, c, b, d, c);
    }
  }
  /** A fan on station `i` facing +Y (`up`) or -Y. */
  const cap = (i: number, up: boolean): void => {
    const ring = rings[i]!;
    const centre = pos.length / 3;
    pos.push(0, ring.y, ring.dz ?? 0);
    if (shade) shade.push(ring.s ?? 1);
    for (let k = 0; k < seg; k++) {
      const a = i * seg + k;
      const b = i * seg + ((k + 1) % seg);
      if (up) idx.push(centre, a, b);
      else idx.push(centre, b, a);
    }
  };
  // `top` caps the highest station, `bottom` the lowest.
  const hi = down ? 0 : rings.length - 1;
  const lo = down ? rings.length - 1 : 0;
  if (opts.top) cap(hi, true);
  if (opts.bottom) cap(lo, false);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

/** Add a lathe to `b` with per-station shades (stored alongside the geometry while it is built). */
function addLathe(
  b: GeoBuilder,
  rings: readonly Ring[],
  seg: number,
  tone: number,
  opts: { top?: boolean; bottom?: boolean; wave?: (angle: number, i: number) => number; shadeAt?: (x: number, y: number, z: number) => number; xf?: (g: THREE.BufferGeometry) => void } = {},
  shade = 1,
): void {
  const stationShade: number[] = [];
  const g = lathe(rings, seg, { ...opts, shade: stationShade });
  opts.xf?.(g);
  let i = 0;
  b.add(g, tone, shade, (x, y, z) => stationShade[i++]! * (opts.shadeAt ? opts.shadeAt(x, y, z) : 1));
}

/** Every other station (first and last kept): the mid-detail version of a profile. */
function thin(rings: readonly Ring[]): Ring[] {
  const out: Ring[] = [];
  for (let i = 0; i < rings.length; i++) if (i === 0 || i === rings.length - 1 || i % 2 === 0) out.push(rings[i]!);
  return out;
}

// --- looks -----------------------------------------------------------------------------------------

/** Torso cuts (crowd variant ids). */
export const Cut = { lapel: 0, tunic: 1, knit: 2, zip: 3, parka: 4, dress: 5, waistcoat: 6, clerical: 7, armed: 8 } as const;
export const CUT_COUNT = 9;
/** Coat-skirt shapes (crowd variant ids). */
export const Tail = { coat: 0, skirt: 1, seat: 2 } as const;
/** Head variants: jaw + facial hair. */
export const Face = { clean: 0, moustache: 1, sideburns: 2, mutton: 3, beard: 4, female: 5 } as const;
export const FACE_COUNT = 6;
/** Hat variants (crowd), in this order. */
export const HAT_KINDS: readonly Exclude<Headwear, 'none'>[] = ['flatCap', 'trilby', 'headscarf', 'peakedCap', 'policeHelmet', 'bowler'];
export const FootKind = { shoes: 0, boots: 1, heels: 2 } as const;

/** Hair blend weights [volume, length, fringe(+)/quiff(-), recede]. */
export const HAIR_BLEND: Readonly<Record<HairStyle, readonly [number, number, number, number]>> = {
  short: [0, 0, 0.1, 0],
  sidePart: [0.18, 0, -0.35, 0],
  quiff: [0.35, 0, -1, 0],
  mod: [0.4, 0.38, 0.9, 0],
  long: [0.25, 1, 0.15, 0],
  beehive: [1, 0.32, 0.35, 0],
  bob: [0.28, 0.55, 0.85, 0],
  balding: [-0.15, 0, 0, 1],
  crop: [-0.55, -0.2, -0.1, 0.15],
  afro: [0.9, 0.18, 0.2, 0],
};

interface OutfitCut {
  cut: number;
  tail: number;
  tailKind: number;
  /** Tail colour: the coat, or the seat of the trousers under a short jacket. */
  tailTrousers: boolean;
  shirtArms: boolean;
  /** Legs drawn thinner (tights/stockings under a skirt). */
  skirt: boolean;
}

const OUTFITS: Record<Outfit, OutfitCut> = {
  longCoat: { cut: Cut.lapel, tail: 0.52, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  jacket: { cut: Cut.lapel, tail: 0.24, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  suit: { cut: Cut.lapel, tail: 0.22, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  workCoat: { cut: Cut.lapel, tail: 0.4, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  shirtsleeves: { cut: Cut.waistcoat, tail: 0.13, tailKind: Tail.seat, tailTrousers: true, shirtArms: true, skirt: false },
  dressSkirt: { cut: Cut.dress, tail: 0.5, tailKind: Tail.skirt, tailTrousers: false, shirtArms: false, skirt: true },
  miniSkirt: { cut: Cut.dress, tail: 0.3, tailKind: Tail.skirt, tailTrousers: false, shirtArms: false, skirt: true },
  tunic: { cut: Cut.tunic, tail: 0.2, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  knit: { cut: Cut.knit, tail: 0.13, tailKind: Tail.seat, tailTrousers: true, shirtArms: false, skirt: false },
  donkeyJacket: { cut: Cut.zip, tail: 0.2, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  harrington: { cut: Cut.zip, tail: 0.13, tailKind: Tail.seat, tailTrousers: true, shirtArms: false, skirt: false },
  parka: { cut: Cut.parka, tail: 0.44, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  clerical: { cut: Cut.clerical, tail: 0.24, tailKind: Tail.coat, tailTrousers: false, shirtArms: false, skirt: false },
  armed: { cut: Cut.armed, tail: 0.13, tailKind: Tail.seat, tailTrousers: true, shirtArms: false, skirt: false },
};

/** One small box on a joint (cuffs, glasses, cigarette, props, holster): crowd "trim" instances. */
export interface TrimSpec {
  joint: number;
  x: number;
  y: number;
  z: number;
  sx: number;
  sy: number;
  sz: number;
  /** sRGB hex. */
  colour: number;
}

/** Everything about a style that the geometry needs: variants, blend weights, channel colours (sRGB hex). */
export interface FigureLook {
  readonly style: CharacterStyle;
  readonly female: boolean;
  readonly age: number;
  readonly cut: number;
  readonly tail: number;
  readonly tailKind: number;
  readonly face: number;
  /** null = no hair drawn (helmet, headscarf hides it). */
  readonly hair: readonly [number, number, number, number] | null;
  readonly hat: number;
  readonly feet: number;
  readonly shirtArms: boolean;
  readonly skirt: boolean;
  /** Tone channels per kind (sRGB hex). */
  readonly torso: readonly [number, number, number];
  readonly head: readonly [number, number, number];
  readonly hatColours: readonly [number, number, number];
  readonly tailColour: number;
  readonly trims: readonly TrimSpec[];
}

const mulRgb = (hex: number, r: number, g: number, b: number): number => {
  const c = (v: number, k: number): number => Math.max(0, Math.min(255, Math.round(v * k)));
  return (c((hex >> 16) & 255, r) << 16) | (c((hex >> 8) & 255, g) << 8) | c(hex & 255, b);
};

function facialFor(s: CharacterStyle): FacialHair {
  if (s.facial) return s.facial;
  return s.moustache ? 'mutton' : 'none';
}

function hairFor(s: CharacterStyle, female: boolean): HairStyle {
  return s.hair ?? (female ? 'bob' : 'short');
}

/** Resolve a style into geometry choices. `dims` are the proportions the trims are placed with. */
export function figureLook(style: CharacterStyle, dims: Proportions): FigureLook {
  const o = OUTFITS[style.outfit];
  const female = style.female ?? o.skirt;
  const age = style.age ?? 0.3;
  const p = style.palette;
  const facial = female ? 'none' : facialFor(style);
  const face = female ? Face.female : facial === 'none' ? Face.clean : Face[facial];
  const hw = style.headwear;
  const hairStyle = hairFor(style, female);
  const hair = hw === 'policeHelmet' ? null : HAIR_BLEND[hairStyle];
  const tie = style.tie ?? (style.outfit === 'suit' || style.outfit === 'armed');
  // Lips: a darker, redder skin for men; lipstick on younger women.
  const lip = female && age < 0.6 ? mulRgb(0x8a2a2a, 1, 1, 1) : mulRgb(p.skin, 0.8, 0.58, 0.55);
  let torso: [number, number, number] = [p.coat, p.shirt, tie ? p.accent : p.shirt];
  if (o.cut === Cut.zip) torso = [p.coat, p.shirt, style.outfit === 'donkeyJacket' ? p.accent : p.coat];
  if (o.cut === Cut.dress) torso = [p.coat, p.shirt, p.accent];
  if (o.cut === Cut.tunic) torso = [p.coat, p.shirt, p.accent];
  const police = hw === 'peakedCap' && (style.outfit === 'armed' || style.outfit === 'tunic');
  const hatColours: [number, number, number] =
    hw === 'peakedCap' ? (police ? [p.headwear, 0x10141f, 0xe6e2d8] : [p.headwear, p.accent, p.accent]) : hw === 'trilby' || hw === 'bowler' ? [p.headwear, mulRgb(p.headwear, 0.55, 0.55, 0.55), p.headwear] : [p.headwear, p.accent, p.accent];
  const feet = FootKind[style.shoesKind ?? (female ? 'shoes' : 'shoes')];
  return {
    style,
    female,
    age,
    cut: o.cut,
    tail: o.tail,
    tailKind: o.tailKind,
    face,
    hair,
    hat: hw === 'none' ? -1 : HAT_KINDS.indexOf(hw),
    feet,
    shirtArms: o.shirtArms,
    skirt: o.skirt,
    torso,
    head: [p.skin, p.hair, lip],
    hatColours,
    tailColour: o.tailTrousers ? p.trousers : p.coat,
    trims: trimsFor(style, dims, o),
  };
}

function propTrims(prop: CarriedProp, d: Proportions, out: TrimSpec[]): void {
  const hs = d.handSize;
  const at = (joint: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, colour: number): void => {
    out.push({ joint, x, y, z, sx, sy, sz, colour });
  };
  switch (prop) {
    case 'briefcase':
      at(Joint.handL, 0, -hs * 2.6, 0.01, 0.075, 0.22, 0.3, 0x3a2416);
      break;
    case 'handbag':
      at(Joint.handL, 0, -hs * 2.2, 0.0, 0.08, 0.15, 0.2, 0x2a1d16);
      break;
    case 'shopping':
      at(Joint.handL, 0, -hs * 2.6, 0.0, 0.15, 0.2, 0.26, 0x6a5a3a);
      break;
    case 'stick':
      at(Joint.handR, 0, -0.42, 0.06, 0.03, 0.82, 0.03, 0x3a2618);
      break;
    case 'umbrella':
      at(Joint.handR, 0, -0.38, 0.02, 0.05, 0.74, 0.05, 0x141416);
      break;
    case 'newspaper':
      at(Joint.handL, 0.0, -hs * 1.4, 0.08, 0.03, 0.2, 0.16, 0xd8d2c0);
      break;
    case 'crate':
      at(Joint.handL, 0, -hs * 3.2, 0.0, 0.2, 0.2, 0.3, 0x7a5a2a);
      at(Joint.handL, 0, -hs * 1.8, 0.0, 0.17, 0.08, 0.26, 0xe8e6e0);
      break;
    default:
      break;
  }
}

function trimsFor(s: CharacterStyle, d: Proportions, o: OutfitCut): TrimSpec[] {
  const out: TrimSpec[] = [];
  const L = d.limb;
  const r = d.headRadius;
  const hc = r * 0.85;
  // Shirt cuffs showing below the sleeve.
  if (!o.shirtArms && s.outfit !== 'parka' && s.outfit !== 'knit' && s.outfit !== 'dressSkirt' && s.outfit !== 'miniSkirt') {
    for (const j of [Joint.lowerArmL, Joint.lowerArmR]) out.push({ joint: j, x: 0, y: -d.lowerArm + 0.008, z: 0, sx: 0.088 * L, sy: 0.024, sz: 0.088 * L, colour: s.palette.shirt });
  }
  if (s.glasses) {
    out.push({ joint: Joint.head, x: 0, y: hc + r * 0.07, z: r * 0.99, sx: r * 1.0, sy: r * 0.2, sz: r * 0.06, colour: 0x1a1612 });
  }
  if (s.smoker) out.push({ joint: Joint.handR, x: 0.012, y: -d.handSize * 1.7, z: d.handSize * 0.55, sx: 0.012, sy: 0.012, sz: 0.07, colour: 0xe8e4da });
  if (s.outfit === 'armed') {
    // Holster on the right hip, a pouch on the left.
    out.push({ joint: Joint.spine, x: -d.waistRadius * 0.95, y: -0.04, z: 0.02, sx: 0.06, sy: 0.15, sz: 0.11, colour: 0x1c1713 });
    out.push({ joint: Joint.spine, x: d.waistRadius * 0.9, y: -0.02, z: 0.04, sx: 0.05, sy: 0.07, sz: 0.08, colour: 0x1c1713 });
  }
  if (s.outfit === 'tunic') {
    // Truncheon pocket flap and the whistle chain.
    out.push({ joint: Joint.spine, x: -d.waistRadius * 0.92, y: -0.07, z: -0.02, sx: 0.04, sy: 0.18, sz: 0.05, colour: 0x111214 });
  }
  if (s.kit === 'apron') {
    // A cloth apron: a bib on the chest and a flap over the lap (on the skirt joint, so it swings).
    const col = s.kitColour ?? 0xe6e2d8;
    const z = d.waistRadius * TAIL_HEM * TAIL_DEPTH * d.depth;
    const flap = Math.max(0.2, o.tail * 0.55 + 0.1);
    out.push({ joint: Joint.coatTail, x: 0, y: -0.02 - flap * 0.5, z: z * 1.15 + 0.012, sx: 0.2 * d.width, sy: flap, sz: 0.012, colour: col });
    out.push({ joint: Joint.chest, x: 0, y: d.shoulderHeight * 0.3, z: 0.69 * d.depth * d.waistRadius + 0.018, sx: 0.13 * d.width, sy: d.shoulderHeight * 0.5, sz: 0.012, colour: col });
  }
  if (s.kit === 'conductor') {
    // Ticket machine on the chest, cash bag on the hip.
    out.push({ joint: Joint.chest, x: -0.03 * d.width, y: -0.01, z: 0.69 * d.depth * d.waistRadius + 0.04, sx: 0.11, sy: 0.085, sz: 0.07, colour: 0x2a2b30 });
    out.push({ joint: Joint.spine, x: d.waistRadius * 0.95, y: -0.07, z: 0.03, sx: 0.05, sy: 0.13, sz: 0.11, colour: 0x3a2416 });
  }
  propTrims(s.prop ?? 'none', d, out);
  return out;
}

// --- torso -----------------------------------------------------------------------------------------

/** Front surface helper for the torso (chest-joint space; the spine section below y = -0.01). */
function torsoSurface(d: Proportions): (x: number, y: number) => number {
  const torsoTop = d.shoulderHeight + 0.04;
  const wb = d.waistRadius * 1.02;
  return (x, y) => {
    let R: number;
    let ds: number;
    if (y >= -0.01) {
      const t = Math.max(0, Math.min(1, (y + 0.01) / (torsoTop + 0.01)));
      R = wb + (d.chestRadius - wb) * t;
      ds = (0.69 - 0.1 * t) * d.depth;
    } else {
      R = d.waistRadius;
      ds = 0.77 * d.depth;
    }
    const xx = Math.min(Math.abs(x), R * 0.98);
    return ds * Math.sqrt(R * R - xx * xx);
  };
}

/**
 * Torso base: chest, waist (on the spine joint) and shoulder caps (on the upper arms). Tone A = coat.
 * Shared by every cut; the cut details go on top (torsoCut).
 */
export function torsoBase(sink: Sink, d: Proportions, detail: Detail): void {
  const h = d.height;
  const w = d.width;
  const dep = d.depth;
  const L = d.limb;
  const high = detail === 'high';
  const seg = high ? 14 : 10;
  const sh = d.shoulderHeight;
  const wb = d.waistRadius * 1.02;
  // Chest: a loft from the waist up through the ribcage to broad, rounded shoulders that slope in to
  // the neck (a V from above, a shoulder highlight from the side), instead of a tapered tube with a
  // rim. The back is a touch flatter than the front.
  const chest = chestRings(d);
  addLathe(sink(Joint.chest), high ? chest : [chest[0]!, chest[3]!, chest[4]!, chest[6]!, chest[7]!], seg, Tone.A, { top: true, shadeAt: (_x, y, z) => (z < -0.05 && y > sh * 0.5 ? 0.95 : 1) });
  // Waist: a short loft under the ribs with a little belly forward (more on the heavy builds, which
  // scale it in depth) and a darker hollow at the small of the back.
  const waist: Ring[] = [
    { y: 0.03, r: wb * 1.0, rz: 0.72 * dep * wb, s: 0.9 },
    { y: -0.06, r: d.waistRadius * 1.02, rz: 0.78 * dep * d.waistRadius, dz: 0.012 * dep, s: 0.97 },
    { y: -d.chest - 0.03, r: d.waistRadius * 0.98, rz: 0.74 * dep * d.waistRadius, s: 0.94 },
  ];
  addLathe(sink(Joint.spine), waist, seg, Tone.A, { bottom: true, xf: (g) => g.translate(0, d.chest, 0), shadeAt: (_x, _y, z) => (z < 0 ? 0.93 : 1) });
  // Waistband over the seam where the coat skirt hangs from the waist (hides the two shapes meeting).
  // Closed, and just wider than the tail's top (TAIL_HEM / TAIL_DEPTH) so nothing shows through the
  // gap between the spine, the band and the skirt from the high camera.
  sink(Joint.spine).add(cyl(d.waistRadius * (TAIL_HEM - 0.02), d.waistRadius * (TAIL_HEM + 0.04), 0.05 * h, high ? seg : 8).scale(1, 1, (TAIL_DEPTH + 0.02) * dep).translate(0, -0.035, 0), Tone.A, 0.8);
  // Shoulder caps: a rounded deltoid on each upper arm (turns with the arm), smaller and lower than
  // the old squashed ledge so the shoulders read as sloping flesh under cloth, not a yoke of armour.
  // Review: 0.076 / 1.1 tall read as football pads from the gameplay camera; a touch smaller and
  // flatter it continues the upper arm's deltoid swell instead of sitting on it.
  for (const j of [Joint.upperArmL, Joint.upperArmR]) sink(j).add(ball(0.069 * L * Math.sqrt(w), high ? 10 : 7, high ? 7 : 4).scale(1, 1.0, 0.92).translate(0, -0.014, 0), Tone.A, 1.02);
}

/** The chest loft's stations in chest-joint space (shared by the torso base and the shirt yoke). */
function chestRings(d: Proportions): Ring[] {
  const sh = d.shoulderHeight;
  const dep = d.depth;
  const w = d.width;
  const wb = d.waistRadius * 1.02;
  const cr = d.chestRadius;
  return [
    { y: -0.02, r: wb, rz: 0.7 * dep * wb, s: 0.9 },
    { y: 0.14 * sh, r: wb * 1.02, rz: 0.68 * dep * wb * 1.02, s: 0.96 },
    { y: 0.5 * sh, r: cr * 0.93, rz: 0.63 * dep * cr * 0.93, s: 1 },
    { y: 0.82 * sh, r: cr * 1.02, rz: 0.6 * dep * cr, s: 1.02 },
    { y: sh - 0.005, r: cr * 1.04, rz: 0.56 * dep * cr, s: 1.03 },
    { y: sh + 0.03, r: cr * 0.86, rz: 0.48 * dep * cr, s: 1.0 },
    { y: sh + 0.055, r: cr * 0.5, rz: 0.36 * dep * cr, s: 0.95 },
    { y: sh + 0.065, r: 0.12 * w, rz: 0.1 * w, s: 0.9 },
  ];
}

/** Cut details on the chest (tones: A coat, B shirt, C tie / accent). */
export function torsoCut(sink: Sink, cut: number, d: Proportions, detail: Detail): void {
  const c = sink(Joint.chest);
  const sp = sink(Joint.spine);
  const w = d.width;
  const h = d.height;
  const sh = d.shoulderHeight;
  const z = torsoSurface(d);
  const high = detail === 'high';
  /** A box lying on the front surface at (x, y), `t` thick. */
  const front = (b: GeoBuilder, bw: number, bh: number, t: number, x: number, y: number, tone: number, shade = 1, rz = 0, rx = -0.08, lift = 0): void => {
    const yy = b === sp ? y + d.chest : y; // spine pieces are authored in chest coordinates
    const zz = z(x, y) + t * 0.5 - 0.004 + lift;
    b.add(box(bw, bh, t).rotateX(rx).rotateZ(rz).translate(x, yy, zz), tone, shade);
  };
  const back = (bw: number, bh: number, t: number, x: number, y: number, tone: number, shade = 1): void => {
    c.add(box(bw, bh, t).rotateX(0.08).translate(x, y, -z(x, y) - t * 0.5 + 0.004), tone, shade);
  };
  const ring = (b: GeoBuilder, rTop: number, rBot: number, hh: number, y: number, tone: number, shade = 1, sz = 0.6): void => {
    const yy = b === sp ? y + d.chest : y;
    b.add(cyl(rTop, rBot, hh, high ? 12 : 10, true).scale(1, 1, sz * d.depth).translate(0, yy, 0), tone, shade);
  };
  const buttons = (n: number, x: number, y0: number, dy: number, tone: number, shade: number, size = 0.022): void => {
    if (!high && tone !== Tone.BRASS) return;
    for (let i = 0; i < n; i++) {
      const y = y0 - i * dy;
      front(y < -0.01 ? sp : c, size, size, 0.014, x, y, tone, shade, 0, -0.08, 0.004);
    }
  };
  const collarY = sh + 0.02 * h;
  const neckR = 0.085 * w;
  switch (cut) {
    case Cut.lapel: {
      // Shirt V and tie, lapels, collar, pocket flaps, a breast pocket and three buttons.
      front(c, 0.1 * w, 0.17 * h, 0.022, 0, sh - 0.085 * h, Tone.B);
      front(c, 0.034, 0.2 * h, 0.012, 0, sh - 0.12 * h, Tone.C, 1, 0, -0.08, 0.008);
      front(c, 0.05, 0.035, 0.02, 0, sh - 0.02 * h, Tone.C, 0.85, 0, -0.08, 0.01);
      for (const s of [1, -1]) {
        front(c, 0.075 * w, 0.22 * h, 0.022, s * 0.07 * w, sh - 0.095 * h, Tone.A, 0.88, s * 0.34, -0.08, 0.012);
        front(c, 0.05 * w, 0.04, 0.02, s * 0.045 * w, collarY - 0.012, Tone.B, 1, s * -0.5, -0.3, 0.012);
        if (high) front(sp, 0.085 * w, 0.03, 0.018, s * 0.09 * w, -0.08, Tone.A, 0.72, 0, -0.04);
      }
      if (!high) break;
      if (high) front(c, 0.07 * w, 0.025, 0.016, 0.1 * w, sh - 0.11 * h, Tone.A, 0.75);
      buttons(3, -0.035 * w, 0.06, 0.07, Tone.A, 0.45);
      // Collar round the back of the neck.
      c.add(cyl(neckR * 1.25, neckR * 1.5, 0.06 * h, 10, true).scale(1, 1, 0.9).translate(0, collarY + 0.005, -0.012), Tone.A, 0.92);
      back(0.2 * w, 0.03, 0.012, 0, sh - 0.02, Tone.A, 0.9);
      break;
    }
    case Cut.tunic: {
      // Closed stand collar, four brass buttons, breast pockets with flaps, epaulettes, belt and buckle.
      c.add(cyl(neckR * 1.05, neckR * 1.22, 0.065 * h, 10, true).translate(0, collarY + 0.008, -0.006), Tone.A, 0.9);
      for (const s of [1, -1]) c.add(box(0.012, 0.016, 0.01).translate(s * neckR * 0.95, collarY + 0.01, neckR * 0.5), Tone.CHROME, 1.1);
      buttons(4, 0, sh - 0.04, 0.085, Tone.BRASS, 1.15, 0.026);
      for (const s of [1, -1]) {
        front(c, 0.1 * w, 0.085, 0.016, s * 0.105 * w, sh - 0.1 * h, Tone.A, 0.88);
        front(c, 0.1 * w, 0.03, 0.02, s * 0.105 * w, sh - 0.065 * h, Tone.A, 0.78, 0, -0.1, 0.006);
        if (high) front(c, 0.018, 0.018, 0.012, s * 0.105 * w, sh - 0.07 * h, Tone.BRASS, 1.1, 0, -0.1, 0.014);
        c.add(box(0.075, 0.018, 0.13).translate(s * d.shoulderHalf * 0.85, sh + 0.035, -0.005), Tone.A, 0.85);
        if (high) c.add(box(0.03, 0.012, 0.02).translate(s * d.shoulderHalf * 0.85, sh + 0.047, 0.03), Tone.CHROME, 1);
      }
      ring(sp, d.waistRadius * 1.05, d.waistRadius * 1.05, 0.045 * h, -d.chest + 0.02, Tone.LEATHER, 1, 0.76);
      front(sp, 0.05, 0.04, 0.012, 0, -d.chest + 0.02, Tone.CHROME, 1.05, 0, 0, 0.008);
      if (high) front(c, 0.008, 0.12, 0.008, 0.06 * w, sh - 0.12 * h, Tone.CHROME, 1, 0.9, -0.08, 0.016);
      break;
    }
    case Cut.knit: {
      // Crew neck with a shirt collar peeping out, ribbed waist, a cable stripe down each side.
      ring(c, neckR * 1.15, neckR * 1.35, 0.05 * h, collarY - 0.005, Tone.A, 0.82, 0.92);
      for (const s of [1, -1]) front(c, 0.045 * w, 0.035, 0.02, s * 0.035 * w, collarY - 0.02, Tone.B, 1, s * -0.6, -0.3, 0.004);
      ring(sp, d.waistRadius * 1.04, d.waistRadius * 1.06, 0.06 * h, -d.chest + 0.0, Tone.A, 0.8, 0.76);
      if (high) for (const s of [1, -1]) front(c, 0.03 * w, 0.24 * h, 0.01, s * 0.075 * w, sh * 0.45, Tone.A, 0.88);
      break;
    }
    case Cut.zip: {
      // Short zip jacket (Harrington) or donkey jacket: collar, zip, slanted pockets, rib waistband,
      // and the shoulder yoke in channel C (black PVC on a donkey jacket, the coat itself otherwise).
      c.add(cyl(neckR * 1.3, neckR * 1.6, 0.06 * h, 10, true).scale(1, 1, 0.92).translate(0, collarY + 0.004, -0.01), Tone.A, 0.9);
      front(c, 0.016, 0.3 * h, 0.01, 0, sh * 0.45, Tone.A, 0.55, 0, -0.06, 0.006);
      front(c, 0.06 * w, 0.06, 0.02, 0, collarY - 0.03, Tone.B, 1, 0, -0.25, 0.002);
      c.add(box(d.shoulderHalf * 2.2, 0.03, 0.2 * d.depth).translate(0, sh + 0.03, -0.01), Tone.C, 1);
      front(c, d.shoulderHalf * 1.9, 0.07, 0.016, 0, sh - 0.015, Tone.C, 1, 0, -0.35, 0.002);
      back(d.shoulderHalf * 1.9, 0.09, 0.016, 0, sh - 0.03, Tone.C, 1);
      if (!high) break;
      for (const s of [1, -1]) front(sp, 0.08 * w, 0.025, 0.016, s * 0.085 * w, -0.06, Tone.A, 0.72, s * 0.5, -0.04);
      ring(sp, d.waistRadius * 1.04, d.waistRadius * 1.04, 0.045 * h, -d.chest + 0.02, Tone.A, 0.78, 0.76);
      if (high) buttons(3, 0.02, sh - 0.06, 0.09, Tone.A, 0.4, 0.026);
      break;
    }
    case Cut.parka: {
      // Fishtail parka: the hood rolled behind the neck with a fur trim, zip, flap pockets with toggles.
      c.add(cyl(neckR * 1.7, neckR * 2.0, 0.09 * h, 12).scale(1, 0.8, 0.85).translate(0, collarY + 0.02, -0.045), Tone.A, 0.9);
      c.add(new THREE.TorusGeometry(neckR * 1.65, 0.022, 5, 12, Math.PI * 1.3).rotateX(Math.PI / 2).rotateY(Math.PI * 0.85 + Math.PI).translate(0, collarY + 0.045, -0.03), Tone.FUR, 1);
      if (!high) break;
      front(c, 0.016, 0.32 * h, 0.012, 0, sh * 0.45, Tone.A, 0.6, 0, -0.06, 0.006);
      for (const s of [1, -1]) {
        front(sp, 0.1 * w, 0.06, 0.02, s * 0.09 * w, -0.07, Tone.A, 0.85);
        front(sp, 0.1 * w, 0.025, 0.022, s * 0.09 * w, -0.04, Tone.A, 0.75, 0, -0.04, 0.004);
        if (high) front(sp, 0.012, 0.03, 0.012, s * 0.09 * w, -0.06, Tone.LEATHER, 1, 0, -0.04, 0.02);
        front(c, 0.08 * w, 0.02, 0.016, s * 0.1 * w, sh - 0.08 * h, Tone.A, 0.75);
      }
      break;
    }
    case Cut.dress: {
      // Round collar (channel B), a line of buttons and a belt (channel C).
      for (const s of [1, -1]) {
        if (high) {
          // HIGH: a rounded Peter Pan flap, flat along the neckline and curved below (two tilted boxes
          // read as paper tags at 2 m).
          const x = s * 0.042 * w;
          const y = collarY + 0.01;
          c.add(new THREE.CylinderGeometry(1, 1, 0.014, 8, 1, false, -Math.PI / 2, Math.PI).rotateX(Math.PI / 2).scale(0.04 * w, 0.046, 1).rotateZ(s * 0.18).rotateX(-0.5).translate(x, y, z(x, collarY - 0.012) + 0.007), Tone.B, 1);
        } else front(c, 0.075 * w, 0.045, 0.016, s * 0.045 * w, collarY - 0.012, Tone.B, 1, s * 0.25, -0.5, 0.004);
      }
      c.add(cyl(neckR * 1.05, neckR * 1.25, 0.04 * h, 10, true).translate(0, collarY + 0.004, -0.004), Tone.B, 0.96);
      buttons(3, 0, sh - 0.07, 0.07, Tone.C, 1, 0.024);
      ring(sp, d.waistRadius * 1.04, d.waistRadius * 1.04, 0.035 * h, -d.chest + 0.04, Tone.C, 0.9, 0.76);
      break;
    }
    case Cut.waistcoat: {
      // Shirt over the shoulders and in the V, tie, waistcoat buttons; the base torso is the waistcoat.
      // The shirt's shoulders are a yoke lofted over the chest's top stations (a flat slab before),
      // with a seam at the bottom edge where it meets the waistcoat. Mid detail keeps the slab: the
      // crowd torso carries every cut, so the loft would be counted for every phone pedestrian.
      if (high) {
        const yoke = chestRings(d)
          .slice(3)
          .map((ring) => ({ ...ring, r: ring.r * 1.035 + 0.003, rz: (ring.rz ?? ring.r) * 1.035 + 0.003 }));
        addLathe(c, yoke, 14, Tone.B, { top: true, bottom: true, shadeAt: (_x, y) => (y < sh * 0.86 ? 0.9 : 1) });
      } else {
        c.add(box(d.shoulderHalf * 2.3, 0.07, 0.22 * d.depth).translate(0, sh + 0.012, -0.01), Tone.B, 1);
      }
      front(c, 0.12 * w, 0.2 * h, 0.022, 0, sh - 0.075 * h, Tone.B, 1, 0, -0.08);
      back(d.shoulderHalf * 1.9, 0.08, 0.016, 0, sh - 0.025, Tone.B, 0.95);
      front(c, 0.032, 0.18 * h, 0.012, 0, sh - 0.11 * h, Tone.C, 1, 0, -0.08, 0.008);
      if (!high) break;
      for (const s of [1, -1]) front(c, 0.05 * w, 0.035, 0.02, s * 0.045 * w, collarY - 0.01, Tone.B, 1, s * -0.5, -0.3, 0.012);
      buttons(4, 0, sh - 0.17 * h, 0.05, Tone.A, 0.45);
      c.add(cyl(neckR * 1.05, neckR * 1.2, 0.045 * h, 10, true).translate(0, collarY + 0.005, -0.004), Tone.B, 0.95);
      break;
    }
    case Cut.clerical: {
      // Black stock and the white dog collar.
      c.add(cyl(neckR * 1.1, neckR * 1.25, 0.05 * h, 10, true).translate(0, collarY + 0.004, -0.004), Tone.WHITE, 1.05);
      front(c, 0.1 * w, 0.16 * h, 0.02, 0, sh - 0.07 * h, Tone.B, 1);
      for (const s of [1, -1]) front(c, 0.07 * w, 0.2 * h, 0.02, s * 0.075 * w, sh - 0.1 * h, Tone.A, 0.85, s * 0.3, -0.08, 0.008);
      buttons(2, -0.035 * w, 0.03, 0.08, Tone.A, 0.5);
      break;
    }
    case Cut.armed: {
      // Police blouson: shirt collar and black tie, zip, epaulettes, a leather belt with holster pouches.
      c.add(cyl(neckR * 1.3, neckR * 1.55, 0.055 * h, 10, true).scale(1, 1, 0.92).translate(0, collarY + 0.004, -0.01), Tone.A, 0.88);
      front(c, 0.085 * w, 0.12 * h, 0.022, 0, sh - 0.055 * h, Tone.B, 1, 0, -0.08);
      front(c, 0.03, 0.12 * h, 0.012, 0, sh - 0.07 * h, Tone.C, 1, 0, -0.08, 0.008);
      for (const s of [1, -1]) {
        front(c, 0.05 * w, 0.035, 0.02, s * 0.04 * w, collarY - 0.012, Tone.B, 1, s * -0.5, -0.3, 0.012);
        front(c, 0.1 * w, 0.03, 0.02, s * 0.105 * w, sh - 0.12 * h, Tone.A, 0.75);
        c.add(box(0.075, 0.018, 0.13).translate(s * d.shoulderHalf * 0.85, sh + 0.035, -0.005), Tone.A, 0.82);
      }
      front(c, 0.016, 0.2 * h, 0.01, 0, sh * 0.35, Tone.A, 0.55, 0, -0.06, 0.006);
      ring(sp, d.waistRadius * 1.07, d.waistRadius * 1.07, 0.05 * h, -d.chest + 0.02, Tone.LEATHER, 1, 0.76);
      front(sp, 0.045, 0.04, 0.012, 0, -d.chest + 0.02, Tone.CHROME, 1, 0, 0, 0.012);
      break;
    }
    default:
      break;
  }
}

// --- coat skirt --------------------------------------------------------------------------------------

/**
 * Coat skirt / skirt / seat top, as multiples of the waist radius (X) and of that times the build depth
 * (Z). Wide enough that the pelvis and the tops of the upper legs (≈1.17 × waist at the sides) stay
 * inside the faceted skirt as the legs swing; narrower and their trouser colour pokes through in a
 * sawtooth along the waist.
 */
export const TAIL_HEM = 1.2;
export const TAIL_DEPTH = 0.86;

/**
 * Coat skirt / skirt / seat of the trousers on the coatTail joint, as a unit shape (radius 1 at the
 * top, length 1 downwards): the crowd scales it per instance, the rig scales it to the style.
 */
export function tailShape(b: GeoBuilder, kind: number, detail: Detail): void {
  const high = detail === 'high';
  const seg = high ? 16 : 10;
  // Closed top (a shaded ledge under the waistband): when the chest twists against the hips (swings,
  // aims, getting into a car) the band no longer covers the skirt's rim, and an open top would show
  // the dark trousers inside as notches at the waist.
  if (kind !== Tail.seat) b.add(new THREE.CircleGeometry(1, seg).rotateX(-Math.PI / 2), Tone.A, 0.78);
  if (kind === Tail.coat) {
    // Coat: hangs straight off the hips, then an A-line flare that quickens towards the hem, with
    // soft vertical folds (shade) and a slight hem wave at full detail; grimier towards the hem band;
    // the front opening as a thin dark strip, a back vent low down.
    const rings: Ring[] = [
      { y: 0, r: 1.0, s: 0.96 },
      { y: -0.28, r: 1.02, s: 1.0 },
      { y: -0.56, r: 1.08, s: 0.98 },
      { y: -0.8, r: 1.17, s: 0.94 },
      { y: -0.93, r: 1.24, s: 0.9 },
      { y: -1, r: 1.27, s: 0.84 },
    ];
    addLathe(b, high ? rings : [rings[0]!, rings[3]!, rings[5]!], seg, Tone.A, {
      wave: high ? (a, i) => (i >= 4 ? 0.025 * Math.sin(a * 5 + 1) : 0) : undefined,
      shadeAt: high ? (x, y, z) => 1 - 0.05 * Math.max(0, -y - 0.4) * (1 + Math.cos(Math.atan2(x, z) * 6 + 0.5)) : undefined,
    });
    if (high) {
      b.add(box(0.1, 1, 0.06).translate(0, -0.5, 1.0).applyMatrix4(new THREE.Matrix4().makeShear(0, 0, 0, -0.4, 0, 0)), Tone.A, 0.62);
      b.add(box(0.06, 0.42, 0.06).translate(0, -0.79, -1.09).applyMatrix4(new THREE.Matrix4().makeShear(0, 0, 0, 0.25, 0, 0)), Tone.A, 0.6);
    }
  } else if (kind === Tail.skirt) {
    // Skirt: fuller flare, pleat shading round the hem, a wave in the hem at full detail.
    const rings: Ring[] = [
      { y: 0, r: 1.0, s: 0.97 },
      { y: -0.3, r: 1.04, s: 1 },
      { y: -0.6, r: 1.13, s: 1 },
      { y: -0.85, r: 1.24, s: 0.97 },
      { y: -1, r: 1.31, s: 0.86 },
    ];
    addLathe(b, high ? rings : thin(rings), seg + 2, Tone.A, {
      wave: high ? (a, i) => (i >= 3 ? 0.03 * Math.sin(a * 8) : 0) : undefined,
      shadeAt: (x, y, z) => (high && y < -0.4 ? 0.94 + 0.06 * Math.cos(Math.atan2(x, z) * 8) : 1),
    });
  } else {
    // Seat of the trousers under a short jacket: little flare.
    b.add(cyl(1, 1.0, 1, seg).translate(0, -0.5, 0), Tone.A, 0.95);
  }
}

// --- limbs ---------------------------------------------------------------------------------------

/** Limb segment kinds (crowd variants of the limb mesh). */
export const Limb = { upperArm: 0, forearm: 1, thigh: 2, shin: 3, shinTights: 4, bareForearm: 5 } as const;
export const LIMB_COUNT = 6;

/**
 * Unit limb profiles (radius 1 at the joint, length 1 down): shaped like flesh under cloth rather
 * than tubes. The lower segments start with a ball at the joint so elbows and knees stay round when
 * they bend (no box seam), and trouser legs flare into a 70s turn-up at the hem.
 */
const LIMB_PROFILES: readonly (readonly Ring[])[] = [
  // Upper arm: deltoid swell near the shoulder, biceps, tapering to the elbow.
  [
    { y: 0.02, r: 0.94, s: 0.96 },
    { y: -0.12, r: 1.06, s: 1.02 },
    { y: -0.42, r: 1.0, s: 1 },
    { y: -0.75, r: 0.88, s: 0.98 },
    { y: -1, r: 0.8, s: 0.94 },
  ],
  // Forearm (sleeve): the elbow, a swell below it, narrowing to the cuff.
  [
    { y: 0, r: 0.8, s: 0.96 },
    { y: -0.1, r: 0.98, s: 1 },
    { y: -0.35, r: 0.92, s: 1 },
    { y: -0.7, r: 0.74, s: 0.98 },
    { y: -1, r: 0.66, s: 0.9 },
  ],
  // Thigh: full at the hip, narrowing to the knee.
  [
    { y: 0.02, r: 1.0, s: 0.96 },
    { y: -0.15, r: 1.03, s: 1 },
    { y: -0.5, r: 0.94, s: 1 },
    { y: -0.85, r: 0.8, s: 0.98 },
    { y: -1, r: 0.76, s: 0.95 },
  ],
  // Shin in trousers: knee, calf, a crease taper, then the flare and turn-up at the hem.
  [
    { y: 0, r: 0.78, s: 0.96 },
    { y: -0.12, r: 0.9, s: 1 },
    { y: -0.32, r: 0.9, s: 1 },
    { y: -0.62, r: 0.72, s: 0.98 },
    { y: -0.86, r: 0.66, s: 0.96 },
    { y: -0.9, r: 0.78, s: 0.84 },
    { y: -1, r: 0.82, s: 0.8 },
  ],
  // Shin in tights or stockings: calf and ankle, no flare.
  [
    { y: 0, r: 0.78, s: 0.96 },
    { y: -0.12, r: 0.9, s: 1 },
    { y: -0.32, r: 0.9, s: 1 },
    { y: -0.62, r: 0.68, s: 0.98 },
    { y: -1, r: 0.5, s: 0.94 },
  ],
  // Bare forearm under a rolled sleeve: elbow, forearm muscle, wrist.
  [
    { y: 0, r: 0.8, s: 0.96 },
    { y: -0.12, r: 0.96, s: 1.02 },
    { y: -0.4, r: 0.86, s: 1 },
    { y: -0.75, r: 0.64, s: 0.98 },
    { y: -1, r: 0.56, s: 0.96 },
  ],
];

/**
 * One limb segment of `kind` (tone A), unit sized unless `r` / `len` are given; `seg` sides. The
 * crowd builds each kind once as a variant and scales per instance; the rig scales to its style.
 */
export function limbShape(b: GeoBuilder, kind: number, detail: Detail, r = 1, len = 1, seg?: number, tone: number = Tone.A): void {
  const high = detail === 'high';
  const sides = seg ?? (high ? 8 : 6);
  const profile = LIMB_PROFILES[kind]!;
  const rings = high ? profile : thin(profile);
  const lower = kind !== Limb.upperArm && kind !== Limb.thigh;
  // Caps only where an open end could show: the far end of a lower segment is always inside a hand
  // or a shoe, an upper segment's top inside the shoulder cap or the pelvis; at full detail the
  // joint ball closes the upper segment's end, at mid detail the segment ends are capped instead.
  addLathe(b, rings, sides, tone, { top: lower && !high, bottom: !lower && !high, xf: (g) => g.scale(r, len, r) });
  // The joint ball (elbow / knee) on the lower segments: it fills the bend from any angle.
  if (lower && high) b.add(ball(profile[0]!.r * r * 1.02, sides, Math.max(4, sides - 3)).scale(1, 0.85, 1), tone, 0.95);
}

/** The crowd's three limb meshes: upper segments, lower segments, and the flared trouser shin. */
export const CrowdLimb = { upper: Limb.upperArm, lower: Limb.forearm, flare: Limb.shin } as const;

/** Unit limb segment for the crowd (compatibility): the old plain tube. */
export function limbUnit(b: GeoBuilder, detail: Detail): void {
  b.add(cyl(1, 0.86, 1, detail === 'high' ? 8 : 6).translate(0, -0.5, 0), Tone.A, 1, (_x, y) => (y < -0.88 ? 0.86 : y > -0.06 ? 0.94 : 1));
}

// --- head ------------------------------------------------------------------------------------------

/**
 * Head base (head joint; tone A skin, B hair for the brows): cranium, cheekbones, brow ridge and
 * brows, eye sockets and eyes, nose, ears, and the neck (neck joint). Shaded so it reads from above
 * and close up: sockets and the underside of the jaw darker, nose and cheeks catching the light.
 */
export function headBase(sink: Sink, d: Proportions, detail: Detail): void {
  const r = d.headRadius;
  const hc = r * 0.85;
  const head = sink(Joint.head);
  const high = detail === 'high';
  const under = (_x: number, y: number): number => (y < hc - r * 0.55 ? 0.82 : 1);
  // Cranium: a touch narrower than deep, flatter at the temples, with the shadow under the jaw and
  // a darker nape where the hair meets the neck.
  head.add(ball(r, high ? 16 : 10, high ? 12 : 8).scale(0.9, 1.0, 0.97).translate(0, hc, 0), Tone.A, 1, (x, y, z) => under(x, y) * (z < -0.5 * r && y < hc ? 0.92 : 1) * (high && Math.abs(x) > r * 0.6 && y > hc + r * 0.1 ? 0.98 : 1));
  // Neck: widens into the trapezius at the base so the head doesn't sit on a peg.
  const nw = d.width;
  const neck: Ring[] = [
    { y: -0.02 * d.height, r: 0.085 * nw, rz: 0.072 * nw, s: 0.78 },
    { y: 0.03 * d.height, r: 0.058 * nw, rz: 0.054 * nw, s: 0.8 },
    { y: 0.09 * d.height, r: 0.052 * nw, rz: 0.05 * nw, s: 0.84 },
    { y: 0.15 * d.height, r: 0.056 * nw, rz: 0.054 * nw, s: 0.86 },
  ];
  addLathe(sink(Joint.neck), high ? neck : [neck[0]!, neck[1]!, neck[3]!], high ? 10 : 6, Tone.A, { bottom: true });
  // Ears.
  for (const s of [1, -1]) head.add(ball(r * 0.22, 6, 5).scale(0.45, 1, 0.75).translate(s * r * 0.88, hc - r * 0.02, -r * 0.02), Tone.A, 0.92);
  if (!high) {
    // Mid detail: a dark band for the eyes and a nose, enough at gameplay distance.
    head.add(box(r * 0.95, r * 0.13, r * 0.08).translate(0, hc + r * 0.06, r * 0.9), Tone.A, 0.55);
    head.add(box(r * 0.22, r * 0.4, r * 0.28).rotateX(0.25).translate(0, hc - r * 0.08, r * 0.92), Tone.A, 1.03);
    head.add(box(r * 0.9, r * 0.09, r * 0.08).translate(0, hc + r * 0.25, r * 0.9), Tone.B, 0.9);
    return;
  }
  // Cheekbones: flat and a touch darker, so they shape the face without reading as rouge.
  for (const s of [1, -1]) head.add(ball(r * 0.25, 6, 5).scale(1, 0.7, 0.75).translate(s * r * 0.44, hc - r * 0.18, r * 0.62), Tone.A, 0.99);
  // Brow ridge, a slight frown line, and eyebrows with an arch.
  head.add(box(r * 1.02, r * 0.14, r * 0.2).translate(0, hc + r * 0.22, r * 0.84), Tone.A, 0.97);
  for (const s of [1, -1]) {
    head.add(box(r * 0.26, r * 0.08, r * 0.07).rotateZ(s * -0.3).translate(s * r * 0.36, hc + r * 0.3, r * 0.94), Tone.B, 0.85);
    head.add(box(r * 0.18, r * 0.07, r * 0.07).rotateZ(s * 0.25).translate(s * r * 0.16, hc + r * 0.27, r * 0.96), Tone.B, 0.85);
  }
  // Eye sockets (shadowed), eyes, and an upper lid that hoods them so they don't read as beads.
  for (const s of [1, -1]) {
    head.add(ball(r * 0.19, 7, 5).scale(1.2, 0.75, 0.55).translate(s * r * 0.31, hc + r * 0.06, r * 0.86), Tone.A, 0.62);
    head.add(ball(r * 0.075, 6, 4).translate(s * r * 0.3, hc + r * 0.06, r * 0.93), Tone.EYE, 1);
    head.add(box(r * 0.2, r * 0.045, r * 0.1).rotateX(0.5).translate(s * r * 0.3, hc + r * 0.125, r * 0.96), Tone.A, 0.8);
  }
  // Nose: bridge, a wedge of a tip, nostrils, and the shadow under it.
  head.add(box(r * 0.18, r * 0.42, r * 0.26).rotateX(0.28).translate(0, hc - r * 0.06, r * 0.93), Tone.A, 1.04);
  head.add(ball(r * 0.12, 6, 5).scale(1.1, 0.9, 1).translate(0, hc - r * 0.25, r * 1.03), Tone.A, 1.02);
  for (const s of [1, -1]) head.add(ball(r * 0.07, 5, 4).translate(s * r * 0.09, hc - r * 0.28, r * 0.97), Tone.A, 0.85);
  head.add(box(r * 0.22, r * 0.04, r * 0.08).translate(0, hc - r * 0.345, r * 0.96), Tone.A, 0.8);
}

/**
 * Jaw, mouth and facial hair (tone A skin, B hair, C lips) for the faces in `faces` (Face ids). Each
 * piece is stored once with the bits of every face that has it (the male jaw under all four kinds of
 * facial hair), so the crowd's head carries all faces cheaply; the rig asks for its one face.
 */
export function headFaces(sink: Sink, faces: readonly number[], d: Proportions, detail: Detail): void {
  const r = d.headRadius;
  const hc = r * 0.85;
  const head = sink(Joint.head);
  const high = detail === 'high';
  let want = 0;
  for (const f of faces) want |= 1 << f;
  const bits = (...ids: number[]): number => ids.reduce((m, id) => m | (1 << id), 0);
  /** Run `fn` for the faces in `natural` that were asked for (skipped if none). */
  const on = (natural: number, fn: () => void): void => {
    const m = natural & want;
    if (!m) return;
    const keep = head.mask;
    head.mask = m;
    fn();
    head.mask = keep;
  };
  const under = (_x: number, y: number): number => (y < hc - r * 0.6 ? 0.78 : 1);
  const F = Face;
  on(bits(F.female), () => {
    head.add(ball(r * 0.6, 10, 8).scale(0.92, 0.85, 0.98).translate(0, hc - r * 0.42, r * 0.2), Tone.A, 1, under);
    head.add(box(r * 0.3, r * 0.08, r * 0.06).translate(0, hc - r * 0.46, r * 0.8), Tone.C, 1);
    if (high) for (const s of [1, -1]) head.add(box(r * 0.2, r * 0.035, r * 0.03).rotateZ(s * 0.2).translate(s * r * 0.3, hc + r * 0.13, r * 0.97), Tone.EYE, 1);
  });
  // Male jaw: squarer, with a chin.
  on(bits(F.clean, F.moustache, F.sideburns, F.mutton, F.beard), () => {
    head.add(ball(r * 0.68, 10, 8).scale(1, 0.82, 1).translate(0, hc - r * 0.45, r * 0.2), Tone.A, 1, under);
    head.add(box(r * 0.42, r * 0.22, r * 0.2).translate(0, hc - r * 0.72, r * 0.62), Tone.A, 0.95, under);
    head.add(box(r * 0.38, r * 0.06, r * 0.06).translate(0, hc - r * 0.47, r * 0.86), Tone.C, 1);
  });
  on(bits(F.moustache, F.mutton, F.beard), () => {
    // A moustache in two wings that droop from the septum, thick in the middle and thin at the
    // tips, drawn darker than the hair and tucked against the lip; a grey one is a dark grey, never
    // the pale bar that read as bared teeth at close range. (Review: 0.68 × a grey head still read
    // as a pale bar at the lineup distance; 0.55 keeps a grey moustache darker than the hair.)
    for (const s of [1, -1]) {
      head.add(box(r * 0.3, r * 0.11, r * 0.1).rotateZ(s * 0.28).translate(s * r * 0.15, hc - r * 0.38, r * 0.93), Tone.B, 0.55, (x) => (Math.abs(x) > r * 0.1 ? 0.92 : 1));
      if (high) head.add(box(r * 0.1, r * 0.2, r * 0.1).rotateZ(s * 0.15).translate(s * r * 0.3, hc - r * 0.46, r * 0.86), Tone.B, 0.53);
    }
    if (high) head.add(box(r * 0.12, r * 0.1, r * 0.1).translate(0, hc - r * 0.36, r * 0.95), Tone.B, 0.57);
  });
  on(bits(F.sideburns, F.beard), () => {
    for (const s of [1, -1]) head.add(box(r * 0.14, r * 0.58, r * 0.3).rotateZ(s * 0.06).translate(s * r * 0.85, hc - r * 0.15, r * 0.2), Tone.B, 0.88);
  });
  on(bits(F.mutton), () => {
    for (const s of [1, -1]) {
      if (high) {
        // HIGH: rounded pads that hug the cheek and flare towards the jaw (the two slabs read flat
        // in three-quarter view), darker at the lower edge.
        head.add(ball(1, 6, 5).scale(r * 0.1, r * 0.4, r * 0.23).translate(s * r * 0.85, hc - r * 0.25, r * 0.2), Tone.B, 0.92);
        head.add(ball(1, 6, 5).scale(r * 0.11, r * 0.19, r * 0.24).rotateZ(s * 0.35).translate(s * r * 0.71, hc - r * 0.6, r * 0.38), Tone.B, 0.9, (_x, y) => (y < hc - r * 0.66 ? 0.86 : 1));
      } else {
        head.add(box(r * 0.14, r * 0.75, r * 0.42).translate(s * r * 0.85, hc - r * 0.25, r * 0.2), Tone.B, 0.92);
        head.add(box(r * 0.16, r * 0.3, r * 0.4).rotateZ(s * 0.35).translate(s * r * 0.7, hc - r * 0.62, r * 0.38), Tone.B, 0.9);
      }
    }
  });
  on(bits(F.beard), () => {
    head.add(ball(r * 0.72, 10, 7).scale(1.02, 0.85, 1.02).translate(0, hc - r * 0.52, r * 0.22), Tone.B, 0.92, (_x, y) => (y > hc - r * 0.3 ? 1 : 0.92));
  });
}

// --- hair --------------------------------------------------------------------------------------------

/**
 * The hair cap with its four blend shapes (head joint, tone A = hair). Base: a close crop following
 * the hairline. aD0 volume (top grows, beehive), aD1 length (back and sides drop to the collar and
 * past it), aD2 fringe (+) or quiff (-), aD3 recede (crown sinks under the scalp: balding).
 */
export function hairCap(b: GeoBuilder, d: Proportions, detail: Detail): void {
  const r = d.headRadius;
  const hc = r * 0.85;
  const W = detail === 'high' ? 16 : 10;
  const H = detail === 'high' ? 12 : 8;
  const R0 = r * 1.05;
  const sx = 0.92;
  const sz = 0.99;
  const smooth = (t: number): number => {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
  };
  const hairline = (cphi: number): number => {
    // θ (from the crown) of the hairline: forehead high at the front, nape low at the back.
    const front = 0.3 * Math.PI;
    const side = 0.5 * Math.PI;
    const back = 0.7 * Math.PI;
    return cphi >= 0 ? side + (front - side) * cphi : side + (back - side) * -cphi;
  };
  const base = b.vertexCount;
  const pos: number[] = [];
  const del: number[][] = [[], [], [], []];
  const maxT = 0.97 * Math.PI;
  for (let iy = 0; iy <= H; iy++) {
    const theta = (iy / H) * maxT;
    for (let ix = 0; ix <= W; ix++) {
      const phi = (ix / W) * Math.PI * 2;
      const sphi = Math.sin(phi);
      const cphi = Math.cos(phi); // +1 = front (+z)
      const hl = hairline(cphi);
      const point = (t: number, R: number): [number, number, number] => [Math.sin(t) * sphi * R * sx, hc + Math.cos(t) * R, Math.sin(t) * cphi * R * sz];
      const inside = theta <= hl;
      const p0 = point(Math.min(theta, hl), R0);
      pos.push(...p0);
      // Volume: the top swells and rises.
      const top = smooth((Math.cos(theta) + 0.1) / 1.0);
      const nx = Math.sin(Math.min(theta, hl)) * sphi;
      const ny = Math.cos(Math.min(theta, hl));
      const nz = Math.sin(Math.min(theta, hl)) * cphi;
      const vol = inside ? r * 0.2 * top : r * 0.06;
      del[0]!.push(nx * vol, ny * vol + (inside ? r * 0.75 * top * top * top : 0), nz * vol);
      // Length: back and sides (not the face) extend past the hairline; long hair falls behind the neck.
      const sideW = smooth((0.55 - cphi) / 0.55);
      let lx = 0;
      let ly = 0;
      let lz = 0;
      if (!inside && sideW > 0) {
        const t = Math.min(theta, 0.78 * Math.PI);
        const q = point(t, R0 * 1.06);
        let qy = q[1];
        if (theta > 0.78 * Math.PI) qy -= (theta - 0.78 * Math.PI) * r * 2.6;
        lx = (q[0] * (theta > 0.78 * Math.PI ? 1.0 : 1) - p0[0]) * sideW;
        ly = (qy - p0[1]) * sideW;
        lz = (q[2] - p0[2] - (theta > 0.78 * Math.PI ? r * 0.15 : 0)) * sideW;
      } else if (inside) {
        // A little bulk all round with length.
        lx = nx * r * 0.05 * sideW;
        lz = nz * r * 0.05 * sideW;
      }
      del[1]!.push(lx, ly, lz);
      // Fringe: front verts past the hairline come down over the forehead; quiff (negative) lifts the front.
      const frontW = smooth((cphi - 0.35) / 0.5);
      let fx = 0;
      let fy = 0;
      let fz = 0;
      if (!inside && frontW > 0) {
        const t = Math.min(theta, 0.4 * Math.PI);
        const q = point(t, R0 * 1.04);
        fx = (q[0] - p0[0]) * frontW;
        fy = (q[1] - p0[1]) * frontW;
        fz = (q[2] - p0[2]) * frontW;
      } else if (inside && theta > 0.12 * Math.PI) {
        const k = frontW * smooth((theta - 0.12 * Math.PI) / (0.18 * Math.PI));
        fx = 0;
        fy = -r * 0.32 * k;
        fz = -r * 0.25 * k;
      }
      del[2]!.push(fx, fy, fz);
      // Recede: the crown and temples sink below the scalp.
      const crown = inside ? smooth((Math.cos(theta) - 0.25) / 0.5) * (1 - 0.6 * smooth(-cphi)) : 0;
      del[3]!.push(-nx * r * 0.16 * crown, -ny * r * 0.16 * crown, -nz * r * 0.16 * crown);
    }
  }
  const idx: number[] = [];
  for (let iy = 0; iy < H; iy++) {
    for (let ix = 0; ix < W; ix++) {
      const a = iy * (W + 1) + ix;
      const c = a + W + 1;
      idx.push(a, c, a + 1, a + 1, c, c + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  // Normals of a sphere are better than of the collapsed hairline.
  const n = g.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < n.count; i++) {
    const x = pos[i * 3]!;
    const y = pos[i * 3 + 1]! - hc;
    const z = pos[i * 3 + 2]!;
    const l = Math.hypot(x, y, z) || 1;
    n.setXYZ(i, x / l, y / l, z / l);
  }
  // Shading that makes the cap read as hair rather than a helmet (art-director pass): a side parting
  // as a dark groove running from the forehead over the crown, soft strand bands round the head
  // (brushed down the sides), and a highlight on the crown.
  const partX = r * 0.24;
  b.add(g, Tone.A, 1, (x, y, z) => {
    const up = y - hc;
    const parting = up > r * 0.3 && z > -r * 0.55 && Math.abs(x - partX) < r * 0.07 ? 0.74 : 1;
    const strands = 1 + 0.045 * Math.sin(Math.atan2(x, z) * 9 + up * 4) * (up < r * 0.55 ? 1 : 0.4);
    return parting * strands * (up > r * 0.6 ? 1.08 : 1);
  });
  if (b.deltas) {
    for (let k = 0; k < 4; k++) {
      const dst = b.deltas[k]!;
      const src = del[k]!;
      for (let i = 0; i < src.length; i++) dst[base * 3 + i] = src[i]!;
    }
  }
}

// --- hats ------------------------------------------------------------------------------------------

/** One hat (head joint; tone A crown, B band, C check / badge accent). */
export function hatShape(b: GeoBuilder, kind: Exclude<Headwear, 'none'>, d: Proportions, detail: Detail): void {
  const r = d.headRadius;
  const hc = r * 0.85;
  const high = detail === 'high';
  switch (kind) {
    case 'flatCap': {
      b.add(cyl(r * 1.06, r * 1.08, r * 0.42, 12).scale(1, 1, 1.2).rotateX(0.14).translate(0, hc + r * 0.72, r * 0.07), Tone.A, 1, (_x, y) => (y > hc + r * 0.85 ? 1.05 : 0.95));
      b.add(ball(r * 1.05, 10, 4).scale(1, 0.32, 1.2).rotateX(0.14).translate(0, hc + r * 0.92, r * 0.07), Tone.A, 1.04);
      b.add(new THREE.CylinderGeometry(r * 0.88, r * 0.88, r * 0.1, 10, 1, false, -Math.PI / 2, Math.PI).scale(1, 1, 0.8).rotateX(0.1).translate(0, hc + r * 0.55, r * 0.66), Tone.A, 0.82);
      if (high) b.add(box(r * 0.12, r * 0.06, r * 0.1).translate(0, hc + r * 1.0, r * 0.75), Tone.A, 0.8);
      break;
    }
    case 'trilby': {
      b.add(cyl(r * 0.76, r * 0.98, r * 0.78, 10).scale(1, 1, 1.12).translate(0, hc + r * 0.98, 0), Tone.A, 1);
      if (high) b.add(box(r * 0.2, r * 0.12, r * 1.4).translate(0, hc + r * 1.38, 0), Tone.A, 0.75); // the pinch
      b.add(cyl(r * 1.6, r * 1.6, r * 0.09, 16).scale(1, 1, 1.06).translate(0, hc + r * 0.62, 0.01), Tone.A, 0.92);
      b.add(cyl(r * 0.99, r * 0.99, r * 0.22, 10, true).scale(1, 1, 1.12).translate(0, hc + r * 0.73, 0), Tone.B, 1);
      break;
    }
    case 'bowler': {
      b.add(ball(r * 1.0, 12, 8).scale(1, 0.95, 1.12).translate(0, hc + r * 0.62, 0), Tone.A, 1.05);
      b.add(cyl(r * 1.36, r * 1.36, r * 0.07, 16).scale(1, 1, 1.12).translate(0, hc + r * 0.6, 0), Tone.A, 0.9);
      b.add(cyl(r * 1.01, r * 1.01, r * 0.16, 12, true).scale(1, 1, 1.12).translate(0, hc + r * 0.72, 0), Tone.B, 1);
      break;
    }
    case 'headscarf': {
      b.add(new THREE.SphereGeometry(r * 1.1, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.68).rotateX(-0.35).translate(0, hc + r * 0.02, -r * 0.05), Tone.A, 1, (x, y) => (high && Math.abs(Math.sin(x * 60 + y * 40)) > 0.85 ? 0.82 : 1));
      b.add(ball(r * 0.24, 6, 5).translate(0, hc - r * 0.88, r * 0.42), Tone.A, 0.92);
      if (high) b.add(box(r * 0.2, r * 0.38, r * 0.06).rotateX(-0.2).translate(r * 0.08, hc - r * 1.1, r * 0.42), Tone.A, 0.88);
      break;
    }
    case 'peakedCap': {
      // Crown, band (B) with chequers (C: white on police caps, the band colour otherwise), badge, peak.
      b.add(cyl(r * 1.16, r * 0.99, r * 0.5, 12).scale(1, 1, 1.1).translate(0, hc + r * 0.98, 0), Tone.A, 1);
      b.add(cyl(r * 1.17, r * 1.17, r * 0.06, 12).scale(1, 1, 1.1).translate(0, hc + r * 1.24, 0), Tone.A, 1.06);
      b.add(cyl(r * 1.0, r * 1.0, r * 0.25, 14, true).scale(1, 1, 1.1).translate(0, hc + r * 0.77, 0), Tone.B, 1);
      if (high) {
        // Chequered band: alternate squares round the cap (C over the band colour B).
        const n = 14;
        for (let i = 0; i < n; i += 2) {
          const a = (i / n) * Math.PI * 2;
          b.add(box(r * 0.42, r * 0.2, r * 0.02).rotateY(a).translate(Math.sin(a) * r * 1.01, hc + r * 0.77, Math.cos(a) * r * 1.11), Tone.C, 1);
        }
      } else {
        b.add(cyl(r * 1.01, r * 1.01, r * 0.08, 14, true).scale(1, 1, 1.1).translate(0, hc + r * 0.77, 0), Tone.C, 0.75);
      }
      b.add(box(r * 0.26, r * 0.26, r * 0.04).translate(0, hc + r * 1.0, r * 1.12), Tone.BRASS, 1.1);
      b.add(new THREE.CylinderGeometry(r * 0.95, r * 0.95, r * 0.06, 10, 1, false, -Math.PI / 2, Math.PI).scale(1, 1, 0.7).rotateX(0.32).translate(0, hc + r * 0.62, r * 0.78), Tone.LEATHER, 1);
      break;
    }
    case 'policeHelmet': {
      // Custodian helmet: tall dome, comb, brim, brass star crest, rose top and chin strap.
      b.add(new THREE.SphereGeometry(r * 1.12, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62).scale(1, 1.7, 1.06).translate(0, hc + r * 0.25, 0), Tone.A, 1, (_x, y) => (y > hc + r * 1.6 ? 1.06 : 1));
      if (high) b.add(box(r * 0.12, r * 0.12, r * 1.5).rotateX(0.1).translate(0, hc + r * 1.75, -r * 0.05), Tone.A, 0.9);
      b.add(cyl(r * 1.16, r * 1.2, r * 0.18, 14).scale(1, 1, 1.08).translate(0, hc + r * 0.3, 0), Tone.A, 0.9);
      if (high) {
        // HIGH: the Brunswick star (eight points from two squares, the turned one a hair behind) under
        // a small crown, instead of a plain brass square.
        b.add(box(r * 0.4, r * 0.4, 0.02).rotateX(-0.25).translate(0, hc + r * 0.97, r * 1.08), Tone.BRASS, 1.2);
        b.add(box(r * 0.4, r * 0.4, 0.02).rotateZ(Math.PI / 4).rotateX(-0.25).translate(0, hc + r * 0.97, r * 1.08 - 0.003), Tone.BRASS, 1.05);
        b.add(box(r * 0.2, r * 0.1, 0.02).rotateX(-0.25).translate(0, hc + r * 1.27, r * 1.04), Tone.BRASS, 1.15);
      } else b.add(box(r * 0.5, r * 0.62, 0.02).rotateX(-0.25).translate(0, hc + r * 0.95, r * 1.08), Tone.BRASS, 1.2);
      if (high) b.add(ball(r * 0.13, 6, 4).scale(1, 1, 0.5).translate(0, hc + r * 0.98, r * 1.12), Tone.A, 0.9);
      b.add(ball(r * 0.15, 6, 4).translate(0, hc + r * 2.15, 0), Tone.CHROME, 1.1);
      b.add(box(r * 0.05, r * 0.06, r * 0.9).rotateX(-0.6).translate(0, hc - r * 0.55, r * 0.55), Tone.LEATHER, 1);
      break;
    }
  }
}

// --- hands and feet ---------------------------------------------------------------------------------

/**
 * A hand (hand joint, tone A): palm, a block of curled fingers and a thumb, in a loose grip that reads
 * holding a gun, a bat or a door handle. `side` +1 = left (the palm faces -X), -1 = right.
 */
export function handShape(b: GeoBuilder, side: 1 | -1, d: Proportions, detail: Detail): void {
  const hs = d.handSize * 1.08;
  const s = side;
  // Palm: flat across the knuckles (Z), thin through the hand (X).
  b.add(box(hs * 0.62, hs * 1.15, hs * 1.12).translate(0, -hs * 0.68, 0.004), Tone.A, 1);
  b.add(cyl(hs * 0.38, hs * 0.42, hs * 0.32, 7).scale(1, 1, 1.3).translate(0, -hs * 0.05, 0), Tone.A, 0.95);
  if (detail === 'high') {
    // Four fingers, each in two curled segments, closing on the palm side in a loose grip that
    // wraps a gun butt, a bat handle or a door handle; the index finger a little straighter (it is
    // the trigger finger); knuckles as a ridge on the back of the hand.
    const fingers: [number, number, number][] = [
      [0.4, 0.86, 0.4],
      [0.14, 1.0, 0.55],
      [-0.14, 0.95, 0.6],
      [-0.4, 0.78, 0.65],
    ];
    for (const [z, len, curl] of fingers) {
      const fw = hs * 0.24;
      const l1 = hs * 0.5 * len;
      const l2 = hs * 0.42 * len;
      const a1 = curl * 0.75;
      const a2 = curl * 1.5;
      // Proximal segment from the knuckle line, then the distal one hinged off its end.
      b.add(box(fw * 0.95, l1, fw).translate(0, -l1 * 0.5, 0).rotateZ(-s * a1).translate(0, -hs * 1.22, z * hs), Tone.A, 0.97, (_x, y) => (y > 0 ? 1.03 : 1));
      const ex = -Math.sin(a1) * l1 * s * -1;
      const ey = -Math.cos(a1) * l1;
      b.add(box(fw * 0.85, l2, fw * 0.92).translate(0, -l2 * 0.5, 0).rotateZ(-s * a2).translate(ex, -hs * 1.22 + ey, z * hs), Tone.A, 0.92);
    }
    b.add(box(hs * 0.2, hs * 0.14, hs * 1.08).translate(s * hs * 0.28, -hs * 1.18, 0.004), Tone.A, 0.9);
    // Thumb: from the heel of the palm, forward and across in two segments.
    b.add(box(hs * 0.3, hs * 0.5, hs * 0.3).translate(0, -hs * 0.25, 0).rotateX(-0.6).rotateZ(s * 0.4).translate(-s * hs * 0.1, -hs * 0.7, hs * 0.5), Tone.A, 1.02);
    b.add(box(hs * 0.26, hs * 0.42, hs * 0.26).translate(0, -hs * 0.21, 0).rotateX(-1.1).rotateZ(s * 0.6).translate(-s * hs * 0.22, -hs * 1.05, hs * 0.72), Tone.A, 0.98);
  } else {
    b.add(box(hs * 0.55, hs * 0.9, hs * 1.02).rotateZ(-s * 0.7).translate(-s * hs * 0.25, -hs * 1.55, 0.004), Tone.A, 0.96);
    b.add(box(hs * 0.32, hs * 0.7, hs * 0.32).rotateX(-0.55).translate(-s * hs * 0.1, -hs * 0.85, hs * 0.58), Tone.A, 1.02);
  }
}

/** A shoe, boot or heeled shoe (foot joint): tone A upper, soles and heels in SOLE. */
export function footShape(b: GeoBuilder, kind: Footwear, d: Proportions, detail: Detail): void {
  const h = d.height;
  const w = d.width;
  const fl = d.footLength;
  const y0 = -d.ankle; // the ground
  const zc = fl * 0.28;
  const high = detail === 'high';
  if (kind === 'heels') {
    b.add(box(0.085 * w, 0.06 * h, fl * 0.82).rotateX(0.18).translate(0, y0 + 0.055 * h, zc - 0.01), Tone.A, 1);
    b.add(ball(0.045 * w, 7, 5).scale(1, 0.55, 1.4).translate(0, y0 + 0.03, zc + fl * 0.36), Tone.A, 1.04);
    b.add(box(0.07 * w, 0.012, fl * 0.38).translate(0, y0 + 0.006, zc + fl * 0.24), Tone.SOLE, 1);
    b.add(box(0.03, 0.06 * h, 0.03).translate(0, y0 + 0.03 * h, zc - fl * 0.4), Tone.SOLE, 1);
    return;
  }
  const boot = kind === 'boots';
  const upperH = boot ? 0.13 * h : 0.075 * h;
  b.add(box(0.105 * w, upperH, fl * 0.92).translate(0, y0 + 0.018 + upperH * 0.5, zc - fl * 0.03), Tone.A, 1, (_x, y) => (y > y0 + upperH * 0.85 ? 0.85 : 1));
  // Rounded toe cap.
  b.add(ball(0.056 * w, 8, 5).scale(1, 0.62, 1.15).translate(0, y0 + 0.04, zc + fl * 0.4), Tone.A, high ? 1.06 : 1);
  // Sole and heel.
  b.add(box(0.11 * w, boot ? 0.03 : 0.02, fl * 1.0).translate(0, y0 + (boot ? 0.015 : 0.01), zc), Tone.SOLE, 1);
  b.add(box(0.1 * w, 0.03, fl * 0.26).translate(0, y0 + 0.022, zc - fl * 0.37), Tone.SOLE, 1);
  if (high && boot) b.add(box(0.11 * w, 0.008, fl * 0.95).translate(0, y0 + 0.032, zc), Tone.FUR, 0.9); // welt stitch (DMs)
  if (high && !boot) b.add(box(0.02, 0.01, fl * 0.3).translate(0, y0 + upperH + 0.02, zc + fl * 0.12), Tone.A, 0.6); // laces
}

/** Unit trim box (crowd): small bevel shading so it doesn't read as a flat sticker. */
export function trimUnit(b: GeoBuilder): void {
  b.add(box(1, 1, 1), Tone.A, 1, (_x, y) => (y > 0.45 ? 1.08 : y < -0.45 ? 0.85 : 1));
}
